import { Stack, Typography } from "@mui/material"
import { makeStyles } from "tss-react/mui"

import { useAppLocalization } from "../../../../localization/components/AppLocalizationProvider"
import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { StandardButton } from "../../../../../shared/components/buttons/StandardButton"
import { StandardDialog } from "../../../../../shared/components/dialogs/StandardDialog"

import type { InfoDisplay } from "../infoDisplaysTypes"
import { CopyLinkField } from "./CopyLinkField"

const useStyles = makeStyles()((theme) => ({
    content: {
        padding: "0 1.5rem 1.5rem",
    },
    subtitle: {
        color: theme.palette.tailwind.slate[500],
    },
    footer: {
        paddingTop: "1.125rem",
        borderTop: `1px solid ${theme.palette.tailwind.slate[200]}`,
    },
    resetButton: {
        "&.MuiButton-root": {
            color: theme.palette.tailwind.red[600],
        },
    },
}))

type Props = Readonly<{
    open: boolean
    display: InfoDisplay
    onClose: () => void
    /** The footer's "Reset link" shortcut, which continues into the reset confirmation. */
    onResetLink: () => void
}>

/** The card's "Permalink / embed" dialog: the display's public link and its embed snippet. */
export function DisplayLinkDialog({ open, display, onClose, onResetLink }: Props) {
    const { classes } = useStyles()
    const { l10n } = useAppLocalization()

    const embedCode = `<iframe src="${display.publicUrl}" width="1920" height="1080" frameborder="0" allowfullscreen></iframe>`

    return (
        <LocalizedStrict id="merchant-info-displays-link-dialog" attrs={{ dialogTitle: true }}>
            <StandardDialog open={open} dialogTitle="Permalink / embed" maxWidthProp="sm" onClose={onClose}>
                <Stack spacing="1.25rem" className={classes.content}>
                    <LocalizedStrict
                        id="merchant-info-displays-link-dialog-subtitle"
                        vars={{ name: l10n.getString(display.nameId) }}
                    >
                        <Typography variant="body2" className={classes.subtitle}>
                            Anyone with this link can view the display.
                        </Typography>
                    </LocalizedStrict>

                    <CopyLinkField
                        labelId="merchant-info-displays-link-dialog-permalink-label"
                        labelFallback="Permalink"
                        value={display.publicUrl}
                    />
                    <CopyLinkField
                        labelId="merchant-info-displays-link-dialog-embed-label"
                        labelFallback="Embed code"
                        value={embedCode}
                        isMultiline
                    />

                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        className={classes.footer}
                    >
                        <LocalizedStrict id="merchant-info-displays-link-dialog-reset-button">
                            <StandardButton
                                variant="text"
                                fullWidth={false}
                                size="small"
                                className={classes.resetButton}
                                onClick={onResetLink}
                            >
                                Reset link
                            </StandardButton>
                        </LocalizedStrict>
                        <LocalizedStrict id="merchant-info-displays-link-dialog-done-button">
                            <StandardButton
                                variant="outlined"
                                color="black"
                                fullWidth={false}
                                size="small"
                                onClick={onClose}
                            >
                                Done
                            </StandardButton>
                        </LocalizedStrict>
                    </Stack>
                </Stack>
            </StandardDialog>
        </LocalizedStrict>
    )
}
